import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { Popper, useMediaQuery } from "@mui/material";
import useCloseOnOutsideScroll from "../../hooks/useCloseOnOutsideScroll";
import useDropdownState, { MOBILE_SIDE_OFFSET } from "../../hooks/useDropdownState";
import SearchableSelectList from "./SearchableSelectList";

import "./SearchableSelect.sass";

const MOBILE_MEDIA_QUERY = "(max-width:460px)";

const DROPDOWN_GAP = 6;
const MOBILE_DROPDOWN_EXTRA_GAP = 8;

const DEFAULT_DROPDOWN_HEIGHT = 280;
const MOBILE_DROPDOWN_MAX_HEIGHT = 320;

/**
 * Dropdown with a search field. The ref gives open / close / toggle,
 * so a button outside (triggerSelector) can open it.
 *
 * @param {object} props
 * @param {{ key: string, value: string, searchText: string }[]} props.options - key is unique (React),
 *   value is what onChange gets; several options may share a value
 * @param {string} props.value - value of the selected option
 * @param {(value: string) => void} props.onChange
 * @param {(option: object) => React.ReactNode} props.renderOption
 * @param {(selectedOption: object|undefined) => React.ReactNode} props.renderValue
 * @param {string} [props.className] - root element
 * @param {string} [props.controlClassName] - control button
 * @param {string} [props.triggerSelector] - outside toggle button
 */
const SearchableSelect = forwardRef(
  (
    {
      options,
      value,
      onChange,
      renderOption,
      renderValue,
      onOpenChange,
      disabled = false,
      className = "",
      controlClassName = "",
      triggerSelector,
    },
    ref,
  ) => {
    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
    const selectRef = useRef(null);
    const popperRef = useRef(null);
    const insideRefs = useMemo(() => [selectRef, popperRef], []);

    const { isOpen, dropdownWidth, open, close, toggle } = useDropdownState({
      anchorRef: selectRef,
      isMobile,
      disabled,
      onOpenChange,
    });

    useImperativeHandle(ref, () => ({ open, close, toggle }), [close, open, toggle]);
    useCloseOnOutsideScroll({ isOpen, onClose: close, insideRefs, triggerSelector });

    const selectedOption = options.find((option) => option.value === value);
    const dropdownGap = isMobile ? DROPDOWN_GAP + MOBILE_DROPDOWN_EXTRA_GAP : DROPDOWN_GAP;

    const handleSelect = (option) => {
      onChange(option.value);
      close();
    };

    return (
      <div ref={selectRef} className={`searchable-select ${className}`}>
        <button
          type="button"
          className={`searchable-select__control ${controlClassName} ${isOpen ? "is-open" : ""}`}
          onClick={toggle}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          {renderValue(selectedOption)}
        </button>

        <Popper
          ref={popperRef}
          open={isOpen && !disabled}
          anchorEl={selectRef.current}
          placement="bottom"
          strategy="absolute"
          className="searchable-select__popper"
          style={{ width: `${dropdownWidth}px` }}
          modifiers={[
            { name: "eventListeners", enabled: true, options: { scroll: true, resize: true } },
            { name: "offset", enabled: true, options: { offset: [0, dropdownGap] } },
            { name: "flip", enabled: false },
            {
              name: "preventOverflow",
              enabled: true,
              options: { padding: MOBILE_SIDE_OFFSET, mainAxis: true, altAxis: false, tether: false },
            },
          ]}
        >
          <SearchableSelectList
            options={options}
            value={value}
            onSelect={handleSelect}
            renderOption={renderOption}
            maxHeight={isMobile ? MOBILE_DROPDOWN_MAX_HEIGHT : DEFAULT_DROPDOWN_HEIGHT}
            autoFocus={!isMobile}
          />
        </Popper>
      </div>
    );
  },
);

SearchableSelect.displayName = "SearchableSelect";

export default SearchableSelect;
